import '../styles/index.css';
import { useState, useCallback, useEffect, useRef } from 'react';
import type { PresentationEvent } from '../../types/presentation.js';
import { ChatBubble } from './ChatBubble.js';
import { NarrationBox } from './NarrationBox.js';
import { SystemMessage } from './SystemMessage.js';
import { TimePassDisplay } from './TimePassDisplay.js';
import { DateChangeDisplay } from './DateChangeDisplay.js';
import { SpeakerIntro } from './SpeakerIntro.js';
import { TransitionOverlay } from './TransitionOverlay.js';

export interface EventPlayerProps {
  /** Array of presentation events to display */
  events: PresentationEvent[];
  /** Called when all events have been processed */
  onComplete?: () => void;
  /** Called when a minigame event is encountered */
  onMinigame?: (minigameId: string, params: Record<string, unknown> | undefined) => void;
  /** Custom class name */
  className?: string;
}

export function EventPlayer({
  events,
  onComplete,
  onMinigame,
  className,
}: EventPlayerProps) {
  const [displayedEvents, setDisplayedEvents] = useState<PresentationEvent[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [activeTransition, setActiveTransition] = useState<PresentationEvent | null>(null);
  const listRef = useRef<HTMLDivElement>(null);

  const isComplete = currentIndex >= events.length;

  // Auto-scroll to bottom with smooth behavior
  useEffect(() => {
    if (listRef.current) {
      listRef.current.scrollTo({
        top: listRef.current.scrollHeight,
        behavior: 'smooth'
      });
    }
  }, [displayedEvents]);

  // Process next event
  const processNextEvent = useCallback(() => {
    if (currentIndex >= events.length) {
      onComplete?.();
      return;
    }

    const event = events[currentIndex];

    // Handle transition events specially (they show fullscreen)
    if (event.type === 'transition') {
      setActiveTransition(event);
      return;
    }

    // Handle minigame events
    if (event.type === 'minigame') {
      onMinigame?.(event.minigameId, event.params);
      setDisplayedEvents(prev => [...prev, event]);
      setCurrentIndex(prev => prev + 1);
      return;
    }

    // Add event to displayed list (messages appear instantly)
    setDisplayedEvents(prev => [...prev, event]);
  }, [currentIndex, events, onComplete, onMinigame]);

  // Start processing when events change or after advancing
  useEffect(() => {
    if (!activeTransition && currentIndex < events.length && displayedEvents.length === currentIndex) {
      processNextEvent();
    }
  }, [currentIndex, activeTransition, events.length, displayedEvents.length, processNextEvent]);

  // Handle click to advance to next message
  const handleClick = useCallback(() => {
    if (!isComplete && !activeTransition) {
      setCurrentIndex(prev => prev + 1);
    }
  }, [isComplete, activeTransition]);

  // Handle transition complete
  const handleTransitionComplete = useCallback(() => {
    setActiveTransition(null);
    setDisplayedEvents(prev => [...prev, events[currentIndex]]);
    setCurrentIndex(prev => prev + 1);
  }, [currentIndex, events]);

  const containerClass = ['pawa-game-container', className].filter(Boolean).join(' ');

  return (
    <div className={containerClass} onClick={handleClick}>
      <div ref={listRef} className="pawa-chat-list">
        {displayedEvents.map((event, index) => (
          <ChatListItem key={index} event={event} />
        ))}
      </div>

      {activeTransition && activeTransition.type === 'transition' && (
        <TransitionOverlay
          type={activeTransition.transitionId}
          durationMs={activeTransition.durationMs}
          onComplete={handleTransitionComplete}
        />
      )}
    </div>
  );
}

interface ChatListItemProps {
  event: PresentationEvent;
}

function ChatListItem({ event }: ChatListItemProps) {
  switch (event.type) {
    case 'say':
      return (
        <ChatBubble
          speaker={event.speaker}
          displayName={event.displayName}
          text={event.text}
          avatarUrl={event.avatarUrl}
          side={event.chatSide || 'left'}
          variant="say"
        />
      );

    case 'think':
      return (
        <ChatBubble
          speaker={event.speaker || 'narrator'}
          displayName={event.displayName || 'Thoughts'}
          text={event.text}
          avatarUrl={event.avatarUrl}
          side={event.chatSide || 'left'}
          variant="think"
        />
      );

    case 'narrate':
      return <NarrationBox text={event.text} />;

    case 'system':
      return <SystemMessage text={event.text} />;

    case 'time_pass':
      return <TimePassDisplay description={event.description} />;

    case 'date_change':
      return <DateChangeDisplay date={event.date} />;

    case 'speaker_intro':
      return (
        <SpeakerIntro
          speakerId={event.speakerId}
          displayName={event.displayName}
        />
      );

    case 'background':
      return (
        <div className="pawa-bg-change-container pawa-chat-item">
          <div className="pawa-bg-change-badge">
            <span>🖼️</span>
            <span>Scene changed</span>
          </div>
        </div>
      );

    case 'transition':
      return null;

    case 'minigame':
      return (
        <div className="pawa-system-container pawa-chat-item">
          <div className="pawa-system-message">
            🎮 Minigame: {event.minigameId}
          </div>
        </div>
      );

    default:
      return null;
  }
}
